import { DBConfig } from 'ngx-indexed-db';
import { ObjectStoreMeta } from 'ngx-indexed-db/lib/ngx-indexed-db.meta';
import { Order, OrdersResultDto } from './dto/order.dto'; // adjust path as needed

/**
 * Схема локальной базы IndexedDB приложения экспедирования ({@code ExpeditorDB}) — фундамент офлайн-
 * работы. Хранилища:
 * - {@code orders} / {@code ordersInWork} / {@code ordersCompleted} — кэш ПОСТРАНИЧНЫХ списков заявок
 *   (ключ [pageId, filter, user]); ускоряют показ и дают список офлайн.
 * - {@code ordersInEditStatus} — локальная РАБОЧАЯ копия заказа (ключ Id): актуальное состояние с
 *   правками пользователя; из неё идут слияние и сохранение, вокруг неё крутится версия бэкапа.
 * - {@code BackupActionsTable} — очередь действий синхронизации бэкапа заказа (составной ключ
 *   [user, Id, orderBackupVersion] — по одному действию на версию заказа).
 * - {@code SyncActionsTable} — очередь отложенных действий (подпись дня, завершение заявки), ключ user.
 * - {@code DocumentsForUpload} — очередь сгенерированных документов на выгрузку (ТТН/ТРН/отчёт).
 * Версия БД и миграции — см. {@link migrationFactory} и {@link dbConfig}.
 */
export const ExpeditorDB: string = 'ExpeditorDB';
export const tableOrders: string = 'orders';
export const tableOrdersInWork: string = 'ordersInWork';
export const tableOrdersCompleted: string = 'ordersCompleted';

/**
 * Одна запись кэша постраничных списков заявок (хранилища {@code orders}, {@code ordersInWork},
 * {@code ordersCompleted}). Хранит ответ API для конкретной страницы, фильтра и пользователя.
 */
export interface OrdersCacheEntry {
  /** Номер страницы */
  pageId: number;
  /** Строковое представление текущего фильтра */
  filter: string;
  /** Идентификатор пользователя (email) */
  user: string;
  /** Сохранённый результат вызова API */
  response: OrdersResultDto;
}

/** Тип для записей в таблице всех заказов */
export type OrdersStoreEntry = OrdersCacheEntry;
/** Тип для записей в таблице заказов в работе */
export type OrdersInWorkStoreEntry = OrdersCacheEntry;
/** Тип для записей в таблице завершённых заказов */
export type OrdersCompletedStoreEntry = OrdersCacheEntry;

export const tableOrdersInEditStatus: string = 'ordersInEditStatus';

export const DOCUMENTS_FOR_UPLOAD_STORE_NAME: string = 'DocumentsForUpload';

/**
 * Направление действия синхронизации бэкапа заказа:
 * - {@code SYNC_FROM_CLIENT} — выгрузить локальные правки реестра на сервер;
 * - {@code SYNC_FROM_SERVER} — подтянуть серверную версию в локальный кэш (безопасно перезагружаема);
 * - {@code OVERWRITE_SERVER} — принудительно записать локальную версию поверх серверной (выбор
 *   пользователя при разрешении конфликта версий).
 * Исходящие ({@code SYNC_FROM_CLIENT}/{@code OVERWRITE_SERVER}) несут несинхронизированные данные и
 * при очистке очереди защищаются от потери (см. {@code backup.service}).
 */
export enum BackupActions {
  OVERWRITE_SERVER = 'OVERWRITE_SERVER',
  SYNC_FROM_SERVER = 'SYNC_FROM_SERVER',
  SYNC_FROM_CLIENT = 'SYNC_FROM_CLIENT'
}
export interface BackupActionsInterface {
  Id: number;
  Order?: Order;
  orderBackupVersion: number;
  backupAction: BackupActions;
  user: string;
  idempotencyKey: string;
}

export interface BackupActionsTableInterface {
  user: string;
  Id: number;
  orderBackupVersion: number;
}

/**
 * Запись локальной рабочей копии заказа (хранилище {@code ordersInEditStatus}, ключ {@code Id}).
 * {@code Order} — текущее локальное состояние заказа, {@code orderBackupVersion} — монотонная версия
 * правок (растёт при каждом сохранении, служит для синхронизации бэкапа), {@code user} — субъект.
 */
export interface OrderInEditStatusInterface {
  Id: number;
  Order: Order;
  orderBackupVersion: number;
  user?: string;
}

export const backupSyncActionsConf = {
  keyPath: ['user', 'Id', 'orderBackupVersion'],
  autoIncrement: false
};

// Ключ кэша постраничных списков заявок: [pageId, filter, user] — своя запись на каждую комбинацию
// страницы, фильтра и субъекта.
export const pkOrderConf = {
  keyPath: ['pageId', 'filter', 'user'],
  autoIncrement: false
};

const pageIdField = {
  name: 'pageId',
  keypath: 'pageId',
  options: { unique: false }
};

const filterField = {
  name: 'filter',
  keypath: 'filter',
  options: { unique: false }
};

const responseField = {
  name: 'response',
  keypath: 'response',
  options: { unique: false }
};

export const userField = { name: 'user', keypath: 'user', options: { unique: false } };

export const pkOrdersInEditStatusConf = { keyPath: 'Id', autoIncrement: false };

const orderIdField = { name: 'Id', keypath: 'Id', options: { unique: true } };

const orderBackupVersion = {
  name: 'orderBackupVersion',
  keypath: 'orderBackupVersion',
  options: { unique: false }
};

const orderField = {
  name: 'Order',
  keypath: 'Order',
  options: { unique: false }
};

const syncActions = {
  name: 'syncActions',
  keypath: 'syncActions',
  options: { unique: false, multiEntry: true }
};

const backupAction = {
  name: 'backupAction',
  keypath: 'backupAction',
  options: { unique: false }
};

const idempotencyKey = {
  name: 'idempotencyKey',
  keypath: 'idempotencyKey',
  options: { unique: false }
};

// Очередь действий бэкапа заказа. Составной первичный ключ [user, Id, orderBackupVersion] → одно
// действие на (пользователь, заказ, версия): разные версии одного заказа — разные записи, тип действия
// (backupAction) в ключ НЕ входит. Индексы по Id/user — для выборки и удаления действий заказа.
const BackupActionsTable = {
  store: 'BackupActionsTable',
  storeConfig: {
    keyPath: ['user', 'Id', 'orderBackupVersion'],
    autoIncrement: false
  },
  storeSchema: [
    userField,
    orderIdField,
    orderField,
    orderBackupVersion,
    backupAction,
    idempotencyKey
  ]
};


// Очередь отложенных действий пользователя (подпись дня, завершение заявки). Ключ — user: одна запись
// на пользователя, внутри массив syncActions. Обрабатывается background-sync при выходе в онлайн.
const SyncActionsTable = {
  store: 'SyncActionsTable',
  storeConfig: {
    keyPath: 'user',
    autoIncrement: false
  },
  storeSchema: [userField, syncActions]
};

export enum DocumentsForUploadRowType {
  FORWARDER_REPORT = 'Отчёт экспедитора',
  TRN = 'Транспортная накладная',
  TTN = 'Торгово-транспортная накладная'
}

export interface DocumentsForUploadRowData {
  data: object;
  docType: DocumentsForUploadRowType;
  forwardingRequestId: number;
  orderNumber: string;
  updatedOn: number;
  uploadedChecksum?: string;
  user: string;
  vehicleDataId: string;
}

// Очередь документов на выгрузку. Составной ключ [user, forwardingRequestId, docType, orderNumber,
// vehicleDataId] → один документ каждого типа на рейс в дне заявки; повторная генерация того же
// документа перезаписывает запись (не плодит дубли).
export const documentsForUploadStoreParams: {
  keyPath: string[];
  autoIncrement: boolean;
} = {
  keyPath: [
    'user',
    'forwardingRequestId',
    'docType',
    'orderNumber',
    'vehicleDataId'
  ],
  autoIncrement: false
};

const documentsForUploadTableStore: ObjectStoreMeta = {
  store: DOCUMENTS_FOR_UPLOAD_STORE_NAME,
  storeConfig: documentsForUploadStoreParams,
  storeSchema: [
    userField,
    {
      keypath: 'forwardingRequestId',
      name: 'forwardingRequestId',
      options: { unique: false }
    },
    {
      keypath: 'docType',
      name: 'docType',
      options: { unique: false }
    },
    {
      keypath: 'orderNumber',
      name: 'orderNumber',
      options: { unique: false }
    },
    {
      keypath: 'vehicleDataId',
      name: 'vehicleDataId',
      options: { unique: false }
    },
    {
      keypath: 'updatedOn',
      name: 'updatedOn',
      options: { unique: false }
    },
    {
      keypath: 'data',
      name: 'data',
      options: { unique: false }
    },
    {
      keypath: 'uploadedChecksum',
      name: 'uploadedChecksum',
      options: { unique: false }
    }
  ]
};

export const baseOrderStore = {
  store: tableOrders,
  storeConfig: pkOrderConf,
  storeSchema: [pageIdField, filterField, responseField, userField]
};

export const ordersInWorkStore = { ...baseOrderStore };
ordersInWorkStore.store = tableOrdersInWork;

export const ordersCompletedStore = { ...baseOrderStore };
ordersCompletedStore.store = tableOrdersCompleted;

export function saveDeleteObjectStore(db: IDBDatabase, tableName: string) {
  // Проверяем наличие объектного хранилища
  const { objectStoreNames } = db;
  if (objectStoreNames.contains(tableName)) {
    db.deleteObjectStore(tableName);
  }
}

export function recreateOrdersObject(db: IDBDatabase, tableName: string) {
  saveDeleteObjectStore(db, tableName);

  const newObjectStore = db.createObjectStore(tableName, pkOrderConf);
  newObjectStore.createIndex(
    pageIdField.name,
    pageIdField.keypath,
    pageIdField.options
  );
  newObjectStore.createIndex(
    filterField.name,
    filterField.keypath,
    filterField.options
  );
  newObjectStore.createIndex(
    responseField.name,
    responseField.keypath,
    responseField.options
  );
  newObjectStore.createIndex(
    userField.name,
    userField.keypath,
    userField.options
  );
}

export function createBackupActionsTable(db: IDBDatabase): void {
  const tableName = 'BackupActionsTable';
  saveDeleteObjectStore(db, tableName);

  const newObjectStore = db.createObjectStore(tableName, backupSyncActionsConf);

  newObjectStore.createIndex(userField.name, userField.keypath, {
    unique: false
  });

  newObjectStore.createIndex(orderIdField.name, orderIdField.keypath, {
    unique: false
  });

  newObjectStore.createIndex(
    orderField.name,
    orderField.keypath,
    orderField.options
  );

  newObjectStore.createIndex(
    orderBackupVersion.name,
    orderBackupVersion.keypath,
    { unique: false }
  );

  newObjectStore.createIndex(
    backupAction.name,
    backupAction.keypath,
    backupAction.options
  );

  newObjectStore.createIndex(
    idempotencyKey.name,
    idempotencyKey.keypath,
    idempotencyKey.options
  );
}

export function createOrdersInEditStatusObject(db: IDBDatabase): void {
  const tableName = tableOrdersInEditStatus;
  saveDeleteObjectStore(db, tableName);

  const newObjectStore = db.createObjectStore(
    tableName,
    pkOrdersInEditStatusConf
  );
  newObjectStore.createIndex(
    orderIdField.name,
    orderIdField.keypath,
    orderIdField.options
  );
  newObjectStore.createIndex(
    orderField.name,
    orderField.keypath,
    orderField.options
  );
  newObjectStore.createIndex(
    orderBackupVersion.name,
    orderBackupVersion.keypath,
    orderBackupVersion.options
  );
}

export function createSyncActionsTable(db: IDBDatabase): void {
  const tableName = 'SyncActionsTable';
  saveDeleteObjectStore(db, tableName);

  const newObjectStore = db.createObjectStore(tableName, {
    keyPath: 'user',
    autoIncrement: false
  });

  newObjectStore.createIndex('user', 'user', { unique: true });

  newObjectStore.createIndex('syncActions', [
    'syncActionsType',
    'actionTargetValue'
  ]);
}

export function createDocumentsForUploadTable(db: IDBDatabase): void {
  saveDeleteObjectStore(db, DOCUMENTS_FOR_UPLOAD_STORE_NAME);
  const store = db.createObjectStore(
    DOCUMENTS_FOR_UPLOAD_STORE_NAME,
    documentsForUploadStoreParams
  );
  store.createIndex('updatedOn', 'updatedOn', { unique: false });
  store.createIndex('user', 'user', { unique: false });
}

function createDB(db: IDBDatabase): void {
  recreateOrdersObject(db, tableOrders);
  recreateOrdersObject(db, tableOrdersInWork);
  recreateOrdersObject(db, tableOrdersCompleted);
  createOrdersInEditStatusObject(db);
  createBackupActionsTable(db);
  createSyncActionsTable(db);
  createDocumentsForUploadTable(db);
}

/**
 * Миграции версий БД (ключ — целевая версия). v1 — первичное создание всех хранилищ ({@link createDB});
 * v2 — удаление легаси-хранилища SmartSeeds. Шаги идемпотентны относительно наличия хранилища
 * ({@link saveDeleteObjectStore} удаляет только существующее), поэтому безопасны на свежих БД.
 */
export function migrationFactory() {
  return {
    1: (db: IDBDatabase /* , transaction: IDBTransaction */) => {
      createDB(db);
    },
    // Версия 2: отказ от SmartSeeds — удаляем легаси-стор учётных данных у существующих пользователей.
    // saveDeleteObjectStore идемпотентен (удаляет только при наличии), поэтому на свежих БД это no-op.
    2: (db: IDBDatabase /* , transaction: IDBTransaction */) => {
      saveDeleteObjectStore(db, 'SmartSeedsTable');
    }
  };
}

export const dbConfig: DBConfig = {
  name: 'ExpeditorDB',
  version: 2,
  objectStoresMeta: [
    baseOrderStore,
    ordersInWorkStore,
    ordersCompletedStore,
    SyncActionsTable,
    BackupActionsTable,
    documentsForUploadTableStore
  ],
  migrationFactory
};
